import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import styles from "./horizontalBarList.module.scss";

interface HorizontalBarListProps {
  title?: string;
  data?: any[];
  labelKey?: string;
  valueKey?: string;
  colorKey?: string;
  valueFormatter?: (value: number) => string;
}

const HorizontalBarList: React.FC<HorizontalBarListProps> = ({
  title = "Horizontal Bar List",
  data = [],
  labelKey = "label",
  valueKey = "value",
  colorKey = "color",
  valueFormatter,
}) => {
  const defaultColors = ["linear-gradient(180deg, #08ADC2 0%, #455391 100%)"];

  // Transform and normalize data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      label:
        item[labelKey] ||
        item.type ||
        item.label ||
        item.name ||
        `Item ${index + 1}`,
      value: Number(item[valueKey] || item.count || item.value || 0),
      color: item[colorKey] || item.color || defaultColors[0],
    }));
  }, [data, labelKey, valueKey, colorKey, defaultColors]);

  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 100;
    return Math.max(...chartData.map((item) => item.value), 1);
  }, [chartData]);

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.listContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
      </Box>

      <Box className={styles.listContainer}>
        {chartData.map((item, index) => (
          <Box key={index} className={styles.listItem}>
            <Box className={styles.labelSection}>
              <Typography className={styles.label}>{item.label}</Typography>
              {/* <Typography className={styles.value}>{item.value}</Typography> */}
              <Typography className={styles.value}>
                {valueFormatter ? valueFormatter(item.value) : item.value}
              </Typography>
            </Box>
            <Box className={styles.barSection}>
              <Box className={styles.barBackground}>
                <Box
                  className={styles.barFill}
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    background: defaultColors[0],
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HorizontalBarList;
